/**
 * Data ownership (plan §14.1, §14.8, §15.1, §23.5; docs/PLAN-EXPORT-POLISH.md §2).
 *
 *   Your data
 *   - Export your Construct, with what is in it stated up front
 *   - Download history, with the checksum and how to verify it
 *   - Transfer to your own GitHub
 *   - Delete your Construct
 *
 * §8.2: export must stay available in READY, SUSPENDED, CANCELLATION_PENDING and RETAINED, so
 * losing a subscription never traps someone's data. This screen enforces that visually: the export
 * card is never disabled by billing state, only by the deployment being gone.
 *
 * THE DISCLOSURE IS THE POINT. "Request an export" used to be a leap of faith: no contents, no
 * size, no idea how long the link lives. Everything the archive contains now comes from
 * `GET /api/exports/estimate`, which is computed from the real corpus and cached server-side, and
 * the two facts that matter most are stated outright: the archive carries the FULL Git history, and
 * the search index is absent because it is derived and rebuilt.
 *
 * Deletion is two-step and irreversible, and the confirmation restates exactly what is destroyed.
 */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ApiError, api } from "../api/client.js";
import { Banner, CodeBlock, Empty, ErrorNote, Modal, formatDate, formatDay } from "../components/ui.js";
import { useSession } from "../state/session.js";
import type { ExportEstimate, ExportJob, GithubTransfer, GithubTransferState } from "../api/types.js";

const EXPORTABLE = new Set(["READY", "SUSPENDED", "CANCELLATION_PENDING", "RETAINED"]);

const EXPORT_COPY: Record<string, string> = {
  REQUESTED: "Queued",
  SNAPSHOTTING: "Gathering your data",
  PACKAGING: "Packaging",
  VERIFYING: "Checking the package",
  AVAILABLE: "Ready to download",
  EXPIRED: "Link expired",
  FAILED: "Did not finish",
};

const TRANSFER_COPY: Record<string, string> = {
  REQUESTED: "Queued",
  PUSHING: "Pushing to GitHub",
  SUCCEEDED: "Done",
  FAILED: "Did not finish",
};

/**
 * The API registers the GitHub transfer endpoints only when it holds GitHub App credentials, and
 * answers 501 otherwise. The build flag keeps the card out of a bundle deployed against an API
 * without them, exactly as VITE_GOOGLE_AUTH does for the Google sign-in button (commit 841e51f).
 * The server's own `enabled` flag is checked too, so the two can never disagree in the customer's
 * favour: both must say yes.
 */
const GITHUB_TRANSFER_BUILD_ENABLED = ["1", "true"].includes(
  String(import.meta.env.VITE_GITHUB_TRANSFER ?? "").toLowerCase(),
);

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 MB";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  return `${(bytes / 1_073_741_824).toFixed(2)} GB`;
}

function formatDuration(seconds: number): string {
  const days = Math.round(seconds / 86_400);
  if (days >= 1) return days === 1 ? "1 day" : `${days} days`;
  const hours = Math.round(seconds / 3600);
  if (hours >= 1) return hours === 1 ? "1 hour" : `${hours} hours`;
  return `${Math.max(1, Math.round(seconds / 60))} minutes`;
}

/**
 * The command a customer runs against the file they just downloaded. Kept character for character
 * in step with `renderReadme()` in src/export/manifest.ts: the archive's own README and this screen
 * must never tell someone two different ways to do the same job.
 *
 * `zstd -d ... -c | tar -x` rather than `tar --zstd`, because `tar.exe` on Windows has no zstd
 * support at all and the piped form works everywhere a customer is likely to be.
 */
function verifyCommand(job: ExportJob): string {
  return [
    "# 1. the file you downloaded is the file we packaged",
    `shasum -a 256 construct-export-v${job.formatVersion}-*.tar.zst`,
    `# expected: ${job.sha256 ?? "(recorded once the export finishes)"}`,
    "",
    "# 2. unpack it, then check every member inside",
    "zstd -d construct-export-*.tar.zst -c | tar -x",
    "shasum -a 256 -c checksums/SHA256SUMS",
    "",
    "# 3. your vault, with its whole history, on your own machine",
    "git clone vault.git.bundle my-vault",
  ].join("\n");
}

export default function DataAndPortability() {
  const { me, refresh } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const [exports, setExports] = useState<ExportJob[] | null>(null);
  const [estimate, setEstimate] = useState<ExportEstimate | null>(null);
  const [listError, setListError] = useState<unknown>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [verifying, setVerifying] = useState<string | null>(null);

  const [deleteStep, setDeleteStep] = useState<0 | 1 | 2>(0);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [confirmToken, setConfirmToken] = useState<string | null>(null);
  const [willDeleteAt, setWillDeleteAt] = useState<string | null>(null);
  const [exportWarning, setExportWarning] = useState<string | null>(null);
  /** Set when delete-confirm demanded a fresher sign-in (§15.4 re-authentication). */
  const [reauthRequired, setReauthRequired] = useState(false);

  const load = useCallback(async () => {
    try {
      const { exports: rows } = await api.exports();
      setExports(rows);
      setListError(null);
    } catch (e) {
      // A 501 here is expected while export is being built — show the screen, not an error page.
      setExports([]);
      setListError(e instanceof ApiError && e.code === "not_implemented" ? null : e);
    }
  }, []);

  const loadEstimate = useCallback(async (refreshIt = false) => {
    try {
      const { estimate: value } = await api.exportEstimate(refreshIt);
      setEstimate(value);
    } catch {
      // The disclosure degrades to the format facts below; it never takes the screen down.
      setEstimate(null);
    }
  }, []);

  useEffect(() => {
    void load();
    void loadEstimate();
  }, [load, loadEstimate]);

  if (!me) return null;
  const deployment = me.deployment;
  const canExport = deployment ? EXPORTABLE.has(deployment.status) : false;

  async function requestExport() {
    setBusy("export");
    setActionError(null);
    try {
      await api.requestExport();
      await load();
    } catch (e) {
      setActionError(e);
    } finally {
      setBusy(null);
    }
  }

  async function download(id: string) {
    setBusy(id);
    setActionError(null);
    try {
      const { url } = await api.exportDownload(id);
      window.location.href = url;
    } catch (e) {
      setActionError(e);
    } finally {
      setBusy(null);
    }
  }

  async function startDeletion() {
    setBusy("delete");
    setActionError(null);
    try {
      const res = await api.deleteRequest(confirmEmail.trim());
      setConfirmToken(res.confirmToken);
      setWillDeleteAt(res.willDeleteAt);
      // §15.4 step 2 "offer/confirm final export" — advisory, never a blocker.
      setExportWarning(res.finalExport?.warning ?? null);
      setReauthRequired(false);
      setDeleteStep(2);
    } catch (e) {
      setActionError(e);
    } finally {
      setBusy(null);
    }
  }

  async function confirmDeletion() {
    if (!confirmToken) return;
    setBusy("delete");
    setActionError(null);
    try {
      await api.deleteConfirm(confirmToken);
      setDeleteStep(0);
      await refresh();
    } catch (e) {
      // A stale session is not a failure, it is a step-up: sign in again, then confirm. The
      // intent is still live (it lasts 15 minutes), so the modal stays open.
      if (e instanceof ApiError && e.details?.reason === "reauthentication_required") {
        setReauthRequired(true);
      } else {
        setActionError(e);
      }
    } finally {
      setBusy(null);
    }
  }

  const notImplemented = actionError instanceof ApiError && actionError.code === "not_implemented";
  // §8.1 does not allow a running Construct to jump straight to DELETION_PENDING: cancelling
  // comes first. The API decides that (src/control/lifecycle.ts) and says so here, so the
  // button explains itself instead of producing a 409 on click.
  const canDelete = me.lifecycle ? me.lifecycle.canDelete : Boolean(deployment);
  const deleteBlockedReason = canDelete ? null : (me.lifecycle?.deleteBlockedReason ?? null);

  return (
    <>
      <div className="page-head">
        <h1>Yours to take</h1>
        <p className="lede">
          Everything in your Construct is durable, versioned Markdown plus your files. You can take
          a full copy at any time, in an open format, whether or not you are still paying us.
        </p>
      </div>

      {notImplemented ? (
        <Banner kind="warn" title="Not available yet">
          Export is still being finished. Nothing has changed on your account.
        </Banner>
      ) : actionError ? (
        <ErrorNote error={actionError} />
      ) : null}
      {listError ? <ErrorNote error={listError} onRetry={() => void load()} /> : null}

      {/* ---- export --------------------------------------------------------- */}
      <div className="card card--accent">
        <div className="card__head">
          <div>
            <h3>Export your Construct</h3>
            <p className="muted" style={{ marginTop: 6 }}>
              A single archive containing your vault history, your stored files, and your structured
              data, with a manifest and checksums so you can verify it.
            </p>
          </div>
        </div>

        <ExportDisclosure estimate={estimate} onRecheck={() => void loadEstimate(true)} />

        {deployment && !canExport ? (
          <Banner kind="warn">Export is not available while your Construct is being set up or removed.</Banner>
        ) : null}
        <div className="btn-row">
          <button
            type="button"
            className="btn btn--primary"
            disabled={!canExport || busy !== null}
            onClick={() => void requestExport()}
          >
            {busy === "export" ? "Requesting" : "Request an export"}
          </button>
        </div>
      </div>

      {/* ---- download history ----------------------------------------------- */}
      <h2 style={{ marginTop: 44, marginBottom: 16 }}>Download history</h2>
      {!exports || exports.length === 0 ? (
        <Empty title="No exports yet">
          <p>When you request one, it will appear here with a checksum and a download link.</p>
        </Empty>
      ) : (
        <>
          <div className="card table-scroll" style={{ padding: 0 }}>
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Requested</th>
                  <th scope="col">Status</th>
                  <th scope="col">Size</th>
                  <th scope="col">Available until</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {exports.map((x) => (
                  <tr key={x.id}>
                    <td className="mono small">{formatDate(x.requestedAt)}</td>
                    <td>{EXPORT_COPY[x.status] ?? x.status}</td>
                    <td className="muted mono small">{x.sizeBytes ? formatBytes(x.sizeBytes) : "not recorded"}</td>
                    <td className="muted mono small">{formatDate(x.expiresAt)}</td>
                    <td>
                      <div className="row">
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          disabled={x.status !== "AVAILABLE" || busy !== null}
                          onClick={() => void download(x.id)}
                        >
                          {busy === x.id ? "Preparing" : "Download"}
                        </button>
                        <button
                          type="button"
                          className="btn btn--link btn--sm"
                          disabled={!x.sha256}
                          onClick={() => setVerifying(verifying === x.id ? null : x.id)}
                        >
                          {verifying === x.id ? "Hide checks" : "Verify"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Verification lives outside the table so the commands are readable and copyable. */}
          {exports
            .filter((x) => x.id === verifying)
            .map((x) => (
              <div className="card" style={{ marginTop: 16 }} key={`verify-${x.id}`}>
                <h3>Check this archive yourself</h3>
                <div className="card__body">
                  <p style={{ marginTop: 0 }}>
                    We recorded this checksum when we packaged the archive. Nothing about it depends
                    on us being around: run these three commands and you have verified the file, its
                    contents, and your vault history without asking us anything.
                  </p>
                  <p className="field__label" style={{ marginTop: 18 }}>
                    Archive sha256
                  </p>
                  <div className="token">{x.sha256}</div>
                  <div style={{ marginTop: 16 }}>
                    <CodeBlock code={verifyCommand(x)} label="Copy commands" />
                  </div>
                  <p className="small muted">
                    On Windows these run as-is in Git Bash. In PowerShell, use{" "}
                    <span className="mono">Get-FileHash -Algorithm SHA256 &lt;file&gt;</span> for step 1, then{" "}
                    <span className="mono">zstd -d construct-export-*.tar.zst</span> followed by{" "}
                    <span className="mono">tar -xf construct-export-*.tar</span> for step 2, because{" "}
                    <span className="mono">tar.exe</span> cannot read zstd itself.
                  </p>
                  <p className="small muted">
                    <span className="mono">vault.git.bundle</span> is absent only when your vault had no
                    commits yet. An empty vault produces no bundle, so step 3 has nothing to clone.
                  </p>
                </div>
              </div>
            ))}
        </>
      )}

      {/* ---- transfer to the customer's own GitHub (plan §14.8) -------------- */}
      <GithubTransferCard
        canTransfer={canExport}
        notice={githubNoticeFrom(searchParams.get("github"))}
        onDismissNotice={() => {
          searchParams.delete("github");
          setSearchParams(searchParams, { replace: true });
        }}
      />

      {/* ---- deletion (§15.1) ------------------------------------------------ */}
      <h2 style={{ marginTop: 44, marginBottom: 16 }}>Delete</h2>
      <div className="card card--danger">
        <h3>Delete your Construct</h3>
        <div className="card__body">
          <p style={{ marginTop: 0 }}>
            This is not the same as cancelling. Deleting permanently destroys:
          </p>
          <ul className="small" style={{ color: "var(--chrome-2)", paddingLeft: 18 }}>
            <li>your vault and its entire version history,</li>
            <li>every file you have stored,</li>
            <li>your structured data and search index,</li>
            <li>the service that runs your Construct.</li>
          </ul>
          <p className="small">
            <strong>There is no undo and no backup we can restore from.</strong> Export first.
          </p>
        </div>
        {deleteBlockedReason ? <Banner kind="warn">{deleteBlockedReason}</Banner> : null}
        <div className="btn-row">
          <button
            type="button"
            className="btn btn--danger"
            disabled={!canDelete || busy !== null}
            onClick={() => setDeleteStep(1)}
          >
            Delete my Construct
          </button>
        </div>
      </div>

      {deleteStep === 1 ? (
        <Modal title="Delete your Construct" onClose={() => setDeleteStep(0)}>
          <p className="modal__id">Step 1 of 2</p>
          <h2>Type your email to continue</h2>
          <p className="muted">
            We will destroy your vault, your files, your database, and the service that runs your
            Construct. This cannot be undone.
          </p>
          <label className="field">
            <span className="field__label">Confirm your email address</span>
            <input
              className="input"
              type="email"
              autoComplete="off"
              value={confirmEmail}
              placeholder={me.account.email}
              onChange={(e) => setConfirmEmail(e.target.value)}
            />
          </label>
          <div className="btn-row">
            <button
              type="button"
              className="btn btn--danger"
              disabled={confirmEmail.trim().toLowerCase() !== me.account.email.toLowerCase() || busy !== null}
              onClick={() => void startDeletion()}
            >
              {busy === "delete" ? "Working" : "Continue"}
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setDeleteStep(0)}>
              Cancel
            </button>
          </div>
        </Modal>
      ) : null}

      {deleteStep === 2 ? (
        <Modal title="Confirm deletion" onClose={() => setDeleteStep(0)}>
          <p className="modal__id">Step 2 of 2</p>
          <h2>Last chance</h2>
          <p className="muted">
            Confirming starts an irreversible deletion{willDeleteAt ? ` on ${formatDay(willDeleteAt)}` : ""}. Every step is
            recorded, and nothing can be recovered afterwards.
          </p>
          {exportWarning ? <Banner kind="warn">{exportWarning}</Banner> : null}
          {reauthRequired ? (
            <Banner kind="warn" title="Sign in again to confirm">
              For something this final we need a fresh sign-in. Open a new magic link from the sign-in
              page, then come back and confirm. This confirmation stays valid for a few more minutes.
            </Banner>
          ) : null}
          <div className="btn-row">
            <button type="button" className="btn btn--danger" disabled={busy !== null} onClick={() => void confirmDeletion()}>
              {busy === "delete" ? "Deleting" : "Delete permanently"}
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setDeleteStep(0)}>
              Keep my Construct
            </button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// disclosure
// ---------------------------------------------------------------------------

/**
 * What is in the archive, how big it is, and how long you have to fetch it.
 *
 * The format facts are rendered from the estimate when it loaded and from the documented defaults
 * when it did not, because "we could not measure your corpus" is not a reason to stop telling
 * someone what an export contains.
 */
function ExportDisclosure({ estimate, onRecheck }: { estimate: ExportEstimate | null; onRecheck: () => void }) {
  const format = estimate?.archiveFormat ?? ".tar.zst";
  // Two different windows, and conflating them is a lie a customer only discovers when a copied
  // link is already dead: the archive lives for `retentionSeconds`, one signed link for
  // `downloadUrlTtlSeconds`. Fall back to the documented defaults when the estimate did not load.
  const retention = estimate ? formatDuration(estimate.retentionSeconds) : "7 days";
  const linkTtl = estimate ? formatDuration(estimate.downloadUrlTtlSeconds) : "1 hour";

  return (
    <div className="card__body" style={{ borderTop: "1px solid var(--hair)", marginTop: 20, paddingTop: 20 }}>
      <div className="row row--between">
        <div>
          <span className="field__label">Estimated size</span>
          <p className="mono" style={{ marginTop: 4, fontSize: "1.1rem", color: "var(--chrome)" }}>
            {estimate
              ? `${estimate.truncated ? "at least" : "about"} ${formatBytes(estimate.archiveBytes)}`
              : "not measured yet"}
          </p>
        </div>
        <button type="button" className="btn btn--link btn--sm" onClick={onRecheck}>
          Recheck
        </button>
      </div>

      {estimate ? (
        <p className="small muted" style={{ marginTop: 6 }}>
          Approximate.{" "}
          {estimate.basis === "previous_export"
            ? "Scaled from the size of your last archive and how much your files have changed since."
            : estimate.vaultBytes === null
              ? "Estimated from your stored files before compression. Your vault's size could not be measured, so the real archive will be larger."
              : "Estimated from your files and vault, before compression, so the real archive is usually smaller."}{" "}
          {!estimate.objectsMeasured
            ? "We could not measure your files right now, so this figure covers only the rest of the archive."
            : estimate.objectCount > 0
              ? `${estimate.truncated ? "At least " : ""}${estimate.objectCount.toLocaleString()} stored files, ${formatBytes(estimate.objectsBytes)} before packaging.`
              : null}
        </p>
      ) : null}

      <p className="field__label" style={{ marginTop: 22 }}>
        What you get
      </p>
      <ul className="small" style={{ color: "var(--chrome-2)", paddingLeft: 18, marginTop: 8 }}>
        {(estimate?.contents ?? FALLBACK_CONTENTS).map((item) => (
          <li key={item.member} style={{ marginBottom: 6 }}>
            <strong style={{ color: "var(--chrome)" }}>{item.label}</strong>{" "}
            <span className="mono muted">{item.member}</span>
            <br />
            {item.detail}
          </li>
        ))}
      </ul>

      <p className="small" style={{ color: "var(--chrome-2)" }}>
        <strong>The archive includes your full Git history.</strong> Not a snapshot of the current
        files: every commit, branch and tag, so you can keep working in your own Git host.
      </p>

      <p className="small muted">
        One file in <span className="mono">{format}</span> format, open and widely supported. The
        archive stays available for {retention} after it is ready. Each download link you generate is
        valid for {linkTtl}; if one expires, press Download again for a fresh link. Your search index
        is not in the archive because it is derived, and rebuilt from the vault and files that are.
      </p>
    </div>
  );
}

/** Used only when the estimate call failed. The facts are the same; only the sizes are missing. */
const FALLBACK_CONTENTS = [
  {
    member: "manifest.json",
    label: "A manifest",
    detail: "A deterministic inventory of everything below, with a checksum for each part.",
  },
  {
    member: "vault.git.bundle",
    label: "Your vault, with its full Git history",
    detail:
      "Every commit, branch and tag, cloneable straight onto your own machine, unless your vault has no commits yet.",
  },
  {
    member: "objects/",
    label: "Every file you have stored",
    detail:
      "Every stored file, plus raw and normalized conversation records, with an index alongside them that carries the exact original key of each one.",
  },
  {
    member: "database/canonical.jsonl",
    label: "Your structured data",
    detail: "The tables the export policy classifies as canonical. Access tokens are never included.",
  },
  {
    member: "checksums/SHA256SUMS",
    label: "Checksums for every member",
    detail: "So you can verify the archive yourself.",
  },
];

// ---------------------------------------------------------------------------
// transfer to my GitHub (plan §14.8)
// ---------------------------------------------------------------------------

const TERMINAL = new Set(["SUCCEEDED", "FAILED"]);

/**
 * The install callback sends people back here with `?github=connected` or `?github=cancelled`
 * (src/api/routes/exports.ts). Backing out of GitHub's install screen is a normal thing to do, so
 * it gets an acknowledgement rather than a silent return to an unchanged screen.
 */
export type GithubNotice = "connected" | "cancelled";

function githubNoticeFrom(param: string | null): GithubNotice | null {
  return param === "connected" || param === "cancelled" ? param : null;
}

function GithubTransferCard({
  canTransfer,
  notice,
  onDismissNotice,
}: {
  canTransfer: boolean;
  notice: GithubNotice | null;
  onDismissNotice: () => void;
}) {
  const [state, setState] = useState<GithubTransferState | null>(null);
  const [transfer, setTransfer] = useState<GithubTransfer | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [repoName, setRepoName] = useState("");
  const [choice, setChoice] = useState<string>("new");
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    if (!GITHUB_TRANSFER_BUILD_ENABLED) return;
    try {
      const next = await api.githubTransferState();
      setState(next);
      setTransfer(next.latest);
      if (!repoName && next.connection) setRepoName("construct-vault");
    } catch {
      // A build flag can outlive the API deploy that justified it. Treat any failure as "off".
      setState({ enabled: false, connection: null, latest: null, history: [] });
    }
  }, [repoName]);

  useEffect(() => {
    void load();
    // `load` is stable enough here: it only closes over `repoName` to seed a default once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const transferId = transfer?.id ?? null;
  const running = transfer !== null && !TERMINAL.has(transfer.status);

  // Poll while a push is in flight, and stop the moment it is not. The dependency is the identity
  // that actually matters, not the whole record: every tick calls setTransfer, and depending on
  // `transfer` would tear the interval down and build a new one on each of them.
  useEffect(() => {
    if (!transferId || !running) return;
    const id = window.setInterval(async () => {
      try {
        const { transfer: next } = await api.githubTransfer(transferId);
        setTransfer(next);
      } catch {
        /* a transient failure just means we look again in two seconds */
      }
    }, 2000);
    return () => window.clearInterval(id);
  }, [transferId, running]);

  if (!GITHUB_TRANSFER_BUILD_ENABLED || (state && !state.enabled)) {
    return (
      <div className="card" style={{ marginTop: 28 }}>
        <div className="card__head">
          <h3>Transfer to your own GitHub</h3>
          <span className="pill pill--idle">Planned</span>
        </div>
        <div className="card__body">
          We will push your vault, with its whole history, into a repository in your own GitHub
          account, so the version history lives with you rather than with us. Until then, the export
          archive carries the same history as a Git bundle you can clone.
        </div>
      </div>
    );
  }

  const connection = state?.connection ?? null;
  const repositories = connection?.repositories ?? [];
  const canCreateRepo = connection?.canCreateRepo ?? false;
  /**
   * A personal account that has granted access to nothing leaves us with no legal destination:
   * GitHub will not let the app create a repository, and there is none to push into. Offering
   * "Type a repository name" here would build a flow the server is guaranteed to refuse with
   * `repo_must_exist`, and would contradict the hint printed directly underneath it.
   */
  const noDestination = !canCreateRepo && repositories.length === 0;
  const choiceOffered = choice === "new" ? canCreateRepo : repositories.some((r) => r.fullName === choice);
  // What the select is actually showing, which is not always what `choice` last held.
  const effectiveChoice = choiceOffered
    ? choice
    : noDestination
      ? ""
      : canCreateRepo
        ? "new"
        : (repositories[0]?.fullName ?? "");
  const targetRepo =
    effectiveChoice === "" ? "" : effectiveChoice === "new" ? repoName.trim() : (effectiveChoice.split("/")[1] ?? "");
  const succeeded = transfer?.status === "SUCCEEDED";

  async function connect() {
    setBusy("connect");
    setError(null);
    try {
      const { url } = await api.githubConnect();
      // A full-page navigation: installing a GitHub App cannot happen inside fetch.
      window.location.href = url;
    } catch (e) {
      setError(e);
      setBusy(null);
    }
  }

  async function disconnect() {
    setBusy("disconnect");
    setError(null);
    try {
      await api.githubDisconnect();
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  async function start() {
    if (!connection || !targetRepo) return;
    setBusy("transfer");
    setError(null);
    try {
      const { transfer: started } = await api.startGithubTransfer({
        owner: connection.targetLogin,
        repo: targetRepo,
        private: true,
      });
      setTransfer(started);
      setConfirming(false);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="card" style={{ marginTop: 28 }}>
      <div className="card__head">
        <div>
          <h3>Transfer to your own GitHub</h3>
          <p className="muted" style={{ marginTop: 6 }}>
            Push your vault, with its whole history, into a repository you own. You can run it again
            whenever you like: later transfers move your branches forward and never touch commits you
            made in that repository yourself.
          </p>
        </div>
        {connection ? <span className="pill pill--ok">Connected</span> : null}
      </div>

      {notice ? (
        <Banner
          kind={notice === "connected" ? "info" : "warn"}
          title={notice === "connected" ? "GitHub connected" : "GitHub was not connected"}
        >
          {notice === "connected" ? "Choose where to put your vault below." : "Nothing changed."}{" "}
          <button type="button" className="btn btn--link btn--sm" onClick={onDismissNotice}>
            Dismiss
          </button>
        </Banner>
      ) : null}
      {error ? <ErrorNote error={error} /> : null}

      {!connection ? (
        <div className="card__body">
          <p style={{ marginTop: 0 }}>
            Connecting installs our GitHub app on the account or organisation you choose. You pick
            which repositories it can see, and you can remove it from GitHub at any time. We never
            store a GitHub password or a personal access token.
          </p>
          <div className="btn-row">
            <button type="button" className="btn btn--primary btn--sm" disabled={busy !== null} onClick={() => void connect()}>
              {busy === "connect" ? "Opening GitHub" : "Connect GitHub"}
            </button>
          </div>
        </div>
      ) : (
        <div className="card__body">
          <p style={{ marginTop: 0 }}>
            Installed on <strong>{connection.targetLogin}</strong>.
          </p>

          <label className="field">
            <span className="field__label">Where should it go</span>
            <select
              className="select"
              value={effectiveChoice}
              onChange={(e) => setChoice(e.target.value)}
              disabled={running || noDestination}
            >
              {canCreateRepo ? <option value="new">Create a new private repository</option> : null}
              {repositories.map((r) => (
                <option key={r.fullName} value={r.fullName}>
                  {r.fullName}
                  {r.private ? "" : " (public)"}
                </option>
              ))}
              {noDestination ? (
                <option value="" disabled>
                  No repository available yet
                </option>
              ) : null}
            </select>
            {!canCreateRepo ? (
              <span className="field__hint">
                On a personal account GitHub does not let an app create repositories. Create an empty
                one on GitHub, add it to the app's repository access, then pick it here.
              </span>
            ) : null}
          </label>

          {effectiveChoice === "new" ? (
            <label className="field">
              <span className="field__label">Repository name</span>
              <input
                className="input"
                value={repoName}
                disabled={running}
                onChange={(e) => setRepoName(e.target.value)}
                placeholder="construct-vault"
              />
              <span className="field__hint">
                It will be created as <span className="mono">{connection.targetLogin}/{repoName.trim() || "construct-vault"}</span>, private.
              </span>
            </label>
          ) : null}

          {transfer ? <TransferProgress transfer={transfer} /> : null}

          <div className="btn-row">
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={!canTransfer || !targetRepo || running || busy !== null}
              onClick={() => setConfirming(true)}
            >
              {running ? "Transferring" : succeeded ? "Transfer again" : "Transfer my vault"}
            </button>
            <button type="button" className="btn btn--ghost btn--sm" disabled={busy !== null || running} onClick={() => void disconnect()}>
              {busy === "disconnect" ? "Removing" : "Disconnect"}
            </button>
          </div>
          {!canTransfer ? (
            <p className="small muted">Transfers are paused while your Construct is being set up or removed.</p>
          ) : null}
        </div>
      )}

      {confirming && connection ? (
        <Modal title="Transfer your vault" onClose={() => setConfirming(false)}>
          <h2>Push to {connection.targetLogin}/{targetRepo}</h2>
          <p className="muted">
            We will push every branch and tag from your vault, with their full history. Branches you
            created in that repository yourself are left alone, and nothing is ever deleted there. If
            the repository has commits we did not put in it, we stop and tell you rather than
            overwrite them.
          </p>
          <div className="btn-row">
            <button type="button" className="btn btn--primary" disabled={busy !== null} onClick={() => void start()}>
              {busy === "transfer" ? "Starting" : "Push my vault"}
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setConfirming(false)}>
              Cancel
            </button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

/** Progress, then either a link to the repository or an explanation of what stopped us. */
function TransferProgress({ transfer }: { transfer: GithubTransfer }) {
  if (transfer.status === "SUCCEEDED") {
    return (
      <Banner kind="info" title="Your vault is on GitHub">
        {transfer.refCount} {transfer.refCount === 1 ? "branch or tag" : "branches and tags"} pushed to{" "}
        {transfer.repoUrl ? (
          <a href={transfer.repoUrl} target="_blank" rel="noreferrer noopener">
            {transfer.targetOwner}/{transfer.targetRepo}
          </a>
        ) : (
          <span className="mono">
            {transfer.targetOwner}/{transfer.targetRepo}
          </span>
        )}
        , finished {formatDate(transfer.completedAt)}.
      </Banner>
    );
  }

  if (transfer.status === "FAILED") {
    return (
      <Banner kind="warn" title={transfer.failureReason === "remote_diverged" ? "We stopped rather than overwrite" : "That did not finish"}>
        {transfer.failureSummary ?? "The transfer did not finish. Nothing on GitHub was changed."}
        {transfer.failureReason === "remote_diverged" ? (
          <p className="small" style={{ marginBottom: 0 }}>
            Choose a different repository, or move those commits onto a branch of their own, and try
            again.
          </p>
        ) : null}
      </Banner>
    );
  }

  return (
    <Banner kind="info">
      {TRANSFER_COPY[transfer.status] ?? transfer.status} to {transfer.targetOwner}/{transfer.targetRepo}.
    </Banner>
  );
}
